import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  sessionExpired: boolean;
  signUp: (email: string, password: string, fullName: string, role?: string, referralCode?: string) => Promise<{ error: Error | null; data: { user: User | null; session: Session | null } | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Track auth events and identify user
        if (event === 'SIGNED_IN' && session?.user) {
          setSessionExpired(false);
          // Identify by opaque id only — no email/PII into PostHog or Sentry (GDPR minimisation).
          analytics.identify(session.user.id);
          analytics.userLoggedIn('password');
          setSentryUser({ id: session.user.id });
        } else if (event === 'SIGNED_OUT') {
          analytics.userLoggedOut();
          analytics.reset();
          clearSentryUser();
        } else if (event === 'TOKEN_REFRESHED' && !session) {
          // Session refresh failed - session expired
          setSessionExpired(true);
          toast.error("Your session has expired. Please sign in again.");
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Identify existing user
      if (session?.user) {
        // Identify by opaque id only — no email/PII into PostHog or Sentry (GDPR minimisation).
        analytics.identify(session.user.id);
        setSentryUser({ id: session.user.id });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, role?: string, referralCode?: string) => {
    // Confirmation links land on /verify-email (not "/"), so the verification page actually handles them.
    const redirectUrl = `${window.location.origin}/verify-email`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          // Carry the requested role so it survives the email-confirmation flow (no session yet to
          // write user_roles). A DB trigger / post-confirm sync can promote from this.
          ...(role && role !== "user" ? { requested_role: role } : {}),
          // Referral code (from a /auth?ref=CODE link) — handle_new_user records the referral.
          ...(referralCode ? { referral_code: referralCode } : {}),
        }
      }
    });

    if (!error) {
      analytics.userSignedUp(role || "user");
    }
    return { error: error as Error | null, data: data ? { user: data.user, session: data.session } : null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, sessionExpired, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
